// Atlas real elevation (Part VI / M30, ELV-2, E-26). PURE + deterministic (ENG-1):
// the DEM *fetch* is client-side (`client/atlas/dem.ts`, like Overpass); the
// resample from a raw elevation tile to a per-column ground height is sim — a
// deterministic transform pinned by unit goldens on the committed KHFD fixture.
//
// A DEM tile is a lat/lon-gridded elevation raster (meters, NW origin). Given the
// Atlas projection (bbox centre + meters/block scale), `ElevationField` inverts a
// world block column back to lat/lon, bilinearly samples the tile, and maps the
// elevation to a block Y with the datum (lowest ground) resting at `ELV.BASE_Y`.
import { WORLD, ELV } from '../config';

export const ELV_BASE_Y = ELV.BASE_Y;
export const NO_DATA = ELV.NO_DATA;

/** Must match `client/atlas/overpass.ts` `projectLatLon` (the forward projection). */
const M_PER_DEG_LAT = 111_320;

export interface LatLon { lat: number; lon: number; }

/** A gridded elevation raster over a lat/lon bbox (row-major, NW origin: row 0 =
 *  the `north` edge, column 0 = the `west` edge). Elevations in meters; a cell may
 *  be `NO_DATA` (sea/void), filled from the nearest valid sample (E-26). */
export interface DemTile {
  width: number;   // samples west→east (columns)
  height: number;  // samples north→south (rows)
  south: number; west: number; north: number; east: number;
  /** `width * height` elevations, meters, `NO_DATA` for gaps. */
  elev: number[];
  /** Provenance for the credits/attribution line (ELV-1). */
  source?: string;
}

/** Datum variant for the resample (ERT-2). Atlas (ELV-2) rests the lowest ground at
 *  `ELV_BASE_Y` (datum = 'min'); Earth (ERT-1) uses a fixed sea-level datum
 *  (datum = 0 m at block-Y 0) and a taller clamp range. Absent ⇒ the Atlas defaults,
 *  so existing worlds stay byte-identical. */
export interface ElevationOpts {
  /** Block-Y the datum elevation rests at (Atlas ELV_BASE_Y = 62; Earth = 0). */
  baseY?: number;
  /** Datum elevation (m): `'min'` = lowest ground over the tile (Atlas ELV-2);
   *  a number = a fixed datum (Earth: 0 = sea level, ERT-1). */
  datum?: 'min' | number;
  /** Ground-Y clamp (Atlas: the default −64..319 range; Earth: its scale-derived range). */
  clampMinY?: number;
  clampMaxY?: number;
}

/** Everything the pure resample needs, threaded through worldgen (serializable). */
export interface AtlasElevation {
  /** The elevation tile, or null when no DEM was available (flat fallback, ELV-1). */
  dem: DemTile | null;
  /** Projection centre (bbox centre) — the inverse-projection origin. */
  center: LatLon;
  /** Meters per block (= the Atlas/Earth scale). */
  scale: number;
  /** Datum/base/range variant (ERT-2 sea-level datum for Earth); absent ⇒ Atlas ELV-2. */
  datum?: ElevationOpts;
}

const clampf = (v: number, lo: number, hi: number): number => (v < lo ? lo : v > hi ? hi : v);
const isBad = (v: number): boolean => v === NO_DATA || !Number.isFinite(v);

/** Inverse of `projectLatLon`: an Atlas block column → the lat/lon it maps from. */
export function unprojectBlock(x: number, z: number, center: LatLon, scale: number): LatLon {
  const east = x * scale;
  const north = -z * scale; // z grows south
  const lat = center.lat + north / M_PER_DEG_LAT;
  const mPerDegLon = M_PER_DEG_LAT * Math.cos((center.lat * Math.PI) / 180);
  const lon = center.lon + east / mPerDegLon;
  return { lat, lon };
}

/** Fill `NO_DATA` cells from the nearest valid sample (E-26): a deterministic
 *  expanding-ring search per hole, reading the ORIGINAL grid so filled holes never
 *  seed one another. All-void → a zero grid (caller then flags `flatTerrain`). */
export function fillNoData(tile: DemTile): Float64Array {
  const { width, height, elev } = tile;
  const out = Float64Array.from(elev);
  const holes: number[] = [];
  for (let i = 0; i < out.length; i++) if (isBad(out[i]!)) holes.push(i);
  if (holes.length === 0) return out;
  if (holes.length === out.length) return out.fill(0);
  const maxRad = width + height;
  for (const i of holes) {
    const r = (i / width) | 0, c = i % width;
    let found = NaN;
    for (let rad = 1; rad <= maxRad && Number.isNaN(found); rad++) {
      for (let dr = -rad; dr <= rad && Number.isNaN(found); dr++) {
        for (let dc = -rad; dc <= rad; dc++) {
          if (Math.max(Math.abs(dr), Math.abs(dc)) !== rad) continue; // ring perimeter only
          const rr = r + dr, cc = c + dc;
          if (rr < 0 || rr >= height || cc < 0 || cc >= width) continue;
          const v = elev[rr * width + cc]!;
          if (!isBad(v)) { found = v; break; }
        }
      }
    }
    out[i] = Number.isNaN(found) ? 0 : found;
  }
  return out;
}

/** Bilinear elevation (m) at (lat,lon) over a NO_DATA-filled tile, clamped to the
 *  tile bounds (queries just outside the bbox read the nearest edge). */
function sampleBilinear(tile: DemTile, filled: Float64Array, lat: number, lon: number): number {
  const { width, height, west, east, south, north } = tile;
  const fc = clampf(((lon - west) / (east - west)) * (width - 1), 0, width - 1);
  const fr = clampf(((north - lat) / (north - south)) * (height - 1), 0, height - 1); // row 0 = north
  const c0 = Math.floor(fc), r0 = Math.floor(fr);
  const c1 = Math.min(c0 + 1, width - 1), r1 = Math.min(r0 + 1, height - 1);
  const tc = fc - c0, tr = fr - r0;
  const g = (r: number, c: number): number => filled[r * width + c]!;
  const top = g(r0, c0) * (1 - tc) + g(r0, c1) * tc;
  const bot = g(r1, c0) * (1 - tc) + g(r1, c1) * tc;
  return top * (1 - tr) + bot * tr;
}

/** A resampled elevation field: block ground-Y per Atlas column (ELV-2). Built
 *  once per world from a DEM tile; a null tile (no DEM) → flat `ELV_BASE_Y`. */
export class ElevationField {
  readonly flatTerrain: boolean;
  /** Datum elevation (meters) that rests at `baseY`: the lowest ground over the tile
   *  (Atlas 'min'), or a fixed datum such as sea level (Earth 0). */
  readonly datumElev: number;
  private readonly tile: DemTile | null;
  private readonly filled: Float64Array | null;
  private readonly center: LatLon;
  private readonly scale: number;
  /** Block-Y the datum rests at (Atlas ELV_BASE_Y; Earth sea-level 0). */
  private readonly baseY: number;
  private readonly clampMinY: number;
  private readonly clampMaxY: number;

  constructor(dem: DemTile | null, center: LatLon, scale: number, opts: ElevationOpts = {}) {
    this.center = center;
    this.scale = scale;
    this.baseY = opts.baseY ?? ELV_BASE_Y;
    this.clampMinY = opts.clampMinY ?? WORLD.MIN_Y + 1;
    this.clampMaxY = opts.clampMaxY ?? WORLD.MAX_Y - 1;
    const fixedDatum = typeof opts.datum === 'number' ? opts.datum : null;
    if (!dem || dem.elev.length === 0 || dem.width <= 0 || dem.height <= 0) {
      this.tile = null; this.filled = null; this.flatTerrain = true;
      this.datumElev = fixedDatum ?? 0;
      return;
    }
    this.tile = dem;
    this.filled = fillNoData(dem);
    let min = Infinity, any = false;
    for (const v of dem.elev) { if (!isBad(v)) { any = true; if (v < min) min = v; } }
    // A fixed datum (Earth sea-level) makes the field non-flat even if it happens to
    // sit at one level; only a truly empty DEM is `flatTerrain` (flat `baseY`).
    this.flatTerrain = (!any || !Number.isFinite(min)) && fixedDatum === null;
    this.datumElev = fixedDatum ?? (Number.isFinite(min) ? min : 0);
  }

  /** Interpolated elevation (meters) at block column (x,z); bilinear (ELV-2). */
  elevAt(x: number, z: number): number {
    if (!this.tile || !this.filled) return this.datumElev;
    const { lat, lon } = unprojectBlock(x, z, this.center, this.scale);
    return sampleBilinear(this.tile, this.filled, lat, lon);
  }

  /** Ground block-Y at column (x,z): `round((elev − datum)/scale) + baseY`, clamped
   *  to the build range. No DEM → flat `baseY` (ELV-2 / ERT-1). */
  groundY(x: number, z: number): number {
    if (this.flatTerrain) return this.baseY;
    const y = Math.round((this.elevAt(x, z) - this.datumElev) / this.scale) + this.baseY;
    return clampf(y, this.clampMinY, this.clampMaxY);
  }
}

/** Bilinear-resample a DEM tile into an elevation field (ELV-2 / ERT-2). The public
 *  entry point named in the spec: `resample(dem, grid, scale)`, with an optional
 *  datum variant for Earth worlds (sea-level datum + a taller clamp range). */
export function resample(dem: DemTile | null, grid: { center: LatLon }, scale: number, opts?: ElevationOpts): ElevationField {
  return new ElevationField(dem, grid.center, scale, opts);
}
